"use client"

import { i18next } from "@/languages/i18n"
import Image from "next/image"
import Link from "next/link"
import { usePathname } from "next/navigation"

// Port của `tamdagroup-nextjs/pages/404.js` (Pages Router + SCSS `.axil-error-not-found`)
// sang App Router + Tailwind. Shape gốc là SVG có PNG base64 nhúng (3.5MB) → đã convert
// sang .webp (52KB); riêng /images/others/404.svg là vector thuần nên giữ nguyên.

const LOCALES = ["en", "cs"] as const
type Locale = (typeof LOCALES)[number] | "vi"

// `not-found.tsx` của App Router KHÔNG nhận `params` nên phải suy locale từ pathname.
// Pathname có thể là URL công khai (`/cs/abc`) hoặc path nội bộ sau rewrite
// (`/sites/tamdagroup.eu/cs/abc`) → quét vài segment đầu thay vì chỉ segment [0].
function localeFromPathname(pathname: string | null): Locale {
  const segments = (pathname ?? "").split("/").filter(Boolean).slice(0, 3)
  return segments.find((s): s is (typeof LOCALES)[number] => LOCALES.includes(s as never)) ?? "vi"
}

export default function CustomNotFoundPage() {
  const locale = localeFromPathname(usePathname())
  // Truyền `lng` tường minh: `t()` của i18n.tsx tự suy locale từ `window`, trên server luôn
  // ra "vi" → hydration mismatch khi vào /en hoặc /cs.
  const t = (key: string) => i18next.t(key, { lng: locale }).toString()

  return (
    // <section> chứ không phải <main>: cả hai site layout đã bọc children trong <main> rồi
    // (src/sites/*/layout.tsx) — lồng <main> trong <main> là HTML không hợp lệ.
    <section className="relative isolate flex w-full flex-col overflow-hidden bg-[linear-gradient(180deg,#EBFFFD_0%,#EFEEFA_100%)]">
      {/* shape-03: backdrop. Mobile phóng rộng hơn khung nhìn để không thành một dải mỏng ở đỉnh. */}
      <Image
        src="/images/shape/404-02.webp"
        alt=""
        aria-hidden
        width={1920}
        height={761}
        priority
        sizes="(max-width: 640px) 220vw, 100vw"
        className="pointer-events-none absolute left-1/2 top-0 -z-10 w-[220%] max-w-none -translate-x-1/2 select-none sm:w-full"
      />

      {/*
        shape-04: nhân vật góc trái. Bản gốc ẩn hẳn trên mobile, nhưng ở đây khối chữ ngắn hơn
        nên phần dưới bị trống — giữ lại ở kích thước lớn hơn để lấp chỗ đó (xem `pb-` bên dưới).
      */}
      <Image
        src="/images/shape/404-03.webp"
        alt=""
        aria-hidden
        width={1092}
        height={826}
        sizes="(max-width: 640px) 78vw, 27vw"
        className="pointer-events-none absolute bottom-0 left-0 w-[78%] max-w-[546px] select-none sm:w-[27%]"
      />

      {/* shape-01 / shape-02: hai đốm sáng nhỏ, chỉ trang trí nên bỏ trên mobile */}
      <Image
        src="/images/shape/404-01.webp"
        alt=""
        aria-hidden
        width={134}
        height={56}
        sizes="67px"
        className="pointer-events-none absolute bottom-[19%] left-[37%] hidden w-[67px] select-none sm:block"
      />
      <Image
        src="/images/shape/404-01.webp"
        alt=""
        aria-hidden
        width={134}
        height={56}
        sizes="67px"
        className="pointer-events-none absolute bottom-[100px] right-[11%] hidden w-[67px] select-none sm:block"
      />

      {/*
        Bản gốc dùng `.fullscreen { min-height: 100vh }`, nhưng header nằm TRÊN khối này trong
        luồng nên 100vh sinh scrollbar và đẩy phần căn giữa xuống dưới tâm khung nhìn.
        Trừ đi chiều cao header (--site-header-h, khai báo trong src/styles/public-theme.css).
        `svh` thay `vh` để thanh địa chỉ của trình duyệt mobile không làm tràn.
      */}
      <div className="relative z-[2] mx-auto flex min-h-[calc(100svh-var(--site-header-h))] w-full max-w-[1128px] flex-1 items-center justify-center px-4 pb-[210px] pt-12 sm:pb-24 sm:pt-24">
        <div className="relative w-full text-center">
          {/* Watermark "404" nằm sau chữ — bản gốc là `.text-image` absolute + z-index -1 */}
          <Image
            src="/images/others/404.svg"
            alt=""
            aria-hidden
            width={860}
            height={378}
            unoptimized
            className="pointer-events-none absolute left-1/2 top-1/2 -z-10 w-[260px] max-w-none -translate-x-1/2 -translate-y-1/2 select-none sm:w-[415px] xl:w-[720px]"
          />

          <h1 className="mb-4 text-[26px] font-bold leading-tight text-[#0d0e14] sm:mb-5 sm:text-4xl md:text-5xl">
            {t("Page not found")}
          </h1>

          <p className="mx-auto mb-7 max-w-[34ch] text-sm text-[#5d5f6d] sm:mb-[30px] sm:max-w-none sm:text-base md:text-lg">
            {t("Sorry, the content you are looking for does not exist.")}
          </p>

          {/*
            `.axil-button.btn-large.btn-transparent` của bản gốc: cao 56px (mobile 52px),
            viền 1px primary, chữ + mũi tên màu primary, hover đảo thành nền primary chữ trắng.
            Mũi tên là hình tam giác dựng bằng border như bản gốc, không phải icon font.
          */}
          <Link
            href="/"
            className="group inline-flex h-[52px] items-center rounded-[4px] border border-[#18AF4E] px-[25px] text-sm font-bold text-[#18AF4E] transition-all duration-300 hover:bg-[#18AF4E] hover:text-white sm:h-[56px] sm:text-base"
          >
            <span className="pr-3 sm:pr-[25px]">{t("Go back")}</span>
            <span className="h-0 w-0 border-y-[4.5px] border-l-[6px] border-y-transparent border-l-[#18AF4E] transition-colors duration-300 group-hover:border-l-white" />
          </Link>
        </div>
      </div>
    </section>
  )
}
